import React, { useState, useRef, useEffect } from "react";
import Toast from "../Toast";
import {
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalInfoCard,
    StandardModalLabel,
    StandardModalText,
} from "src/components";
import {
    SubText, RowHeader,
    CountText, DocumentGrid,
    DocumentItem, DocumentTitle,
    DocumentCard, UploadButton,
    UploadIcon, UploadText,
    ActionRow, ViewButton,
    RemoveButton, IconBtn,
    FileRow, FileName,
    ModalOverlay, ModalBox, ModalClose,
    RadioWrapper, ConfirmText, RadioCircle,
    RadioCheckIcon, TitleRow,
    PreviewImage, UploadedByText
} from "./DocumentUploader.style";
import AddIcon from "src/img/new/add.svg"; 
import RedoIcon from "src/img/new/refresh-icon.png";
import DownloadIcon from "src/img/download-white2.png";
import CheckIcon from "src/img/Green-Check.svg";
import CloseIcon from "src/img/new/close-white.png";
import BlueCheck from "src/img/Blue-check.svg";

import { deleteLicenseTrialDocument, rejectLicenseTrialDocument, approveLicenseTrialDocument, unapproveLicenseTrialDocument } from "./api/documentsApi";

import {
    getPreviewUrl,
} from "./utils/documentHelpers";
import { MODE_SOURCE } from "./utils/documentHelpers";

export default function DocumentUploader({
    requiredDocuments = [],
    title = "Documents Uploaded",
    color,
    mode = MODE_SOURCE.SETTINGS,
    onChange,
    refresh,
    setHasChanges
}) {

    const inputRefs = useRef([]);
    const [documents, setDocuments] = useState([]);
    const [previewFile, setPreviewFile] = useState(null);
    const [selectedIndexes, setSelectedIndexes] = useState([]);

    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [removeIndex, setRemoveIndex] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        onChange?.({
            documents,
            selected: selectedIndexes.map(i => documents[i]).filter(Boolean)
        });
    }, [selectedIndexes, documents]);

    useEffect(() => {
        let approvedIndexes = [];

        const initialDocs = requiredDocuments.map((doc, index) => {
            if (doc.upload?.url) {
                if (doc.upload?.approvedAt) {
                    approvedIndexes.push(index);
                }

                return {
                    uuid: doc.uuid,
                    uploadUuid: doc.upload.uuid,
                    name: decodeURIComponent(doc.upload.url.split("/").pop()),
                    url: doc.upload.url,
                    uploaded: true,
                    approvedAt: doc.upload.approvedAt,
                    approvedBy: doc.upload.approvedBy,
                    uploadedBy: doc.upload?.uploadedBy
                };
            }
            return null;
        });

        setDocuments(initialDocs);

        if (mode === MODE_SOURCE.SALES_AND_PROMOTION) {
            setSelectedIndexes(approvedIndexes);
        }

        inputRefs.current = [];
    }, [requiredDocuments, mode]);

    const handleUpload = (index, file) => {
        if (!file) return;
        const updated = [...documents];
        updated[index] = file;
        setDocuments(updated);
        setSelectedIndexes(prev => [...prev, index]);
    };

    const openRemoveModal = (index) => {
        const doc = documents[index];
        const isFromApi = !!doc?.uuid;
        if (!isFromApi) {
            handleInstantRemove(index);
            return;
        }
        setRemoveIndex(index);
        setShowRemoveModal(true);
    };

    const handleInstantRemove = (index) => {
        setDocuments(prev => {
            const copy = [...prev];
            copy[index] = null;
            return copy;
        });

        setSelectedIndexes(prev =>
            prev.filter(i => i !== index)
        );
    };

    const cancelRemove = () => {
        setShowRemoveModal(false);
        setRemoveIndex(null);
    };

    const confirmRemove = async () => {
        const doc = documents[removeIndex];
        if (!doc?.uuid) return;

        try {
            setLoading(true);

            if (mode === MODE_SOURCE.SALES_AND_PROMOTION) {
                await rejectLicenseTrialDocument(doc.uploadUuid);
                Toast.success("Document Rejected Successfully");
            } else {
                await deleteLicenseTrialDocument(doc.uuid);
                Toast.success("Document Deleted Successfully");
            }

            setShowRemoveModal(false);
            setRemoveIndex(null);
            setDocuments(prev => {
                const copy = [...prev];
                copy[removeIndex] = null;
                return copy;
            });

            setSelectedIndexes(prev =>
                prev.filter(i => i !== removeIndex)
            );

            await refresh?.();
        } catch (err) {
            console.error("Action failed:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (file) => {
        if (!file) return;

        const url = file?.url || getPreviewUrl(file);
        const filename = file?.name || "document";

        try {
            const response = await fetch(url, {
                method: "GET",
                mode: "cors"
            });

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            a.style.display = "none";

            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed:", err);
            Toast.error("Failed to download file");
        }
    };

    const handleView = async (file) => {
        if (!file) return;

        const url = getPreviewUrl(file);
        if (!url) return;
        const cleanUrl = decodeURIComponent(url.split("?")[0]);
        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(cleanUrl);
        const isPdf = /\.pdf$/i.test(cleanUrl);

        if (isImage) {
            setPreviewFile(file);
            return;
        }
        if (!isPdf) {
            window.open(url, "_blank", "noopener,noreferrer");
            return;
        }

        try {
            const response = await fetch(url);
            const contentType = response.headers.get("Content-Type") || "";
            if (contentType.includes("application/pdf")) {
                window.open(url, "_blank", "noopener,noreferrer");
                return;
            }
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(
                new Blob([blob], { type: "application/pdf" })
            );

            window.open(blobUrl, "_blank", "noopener,noreferrer");
            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);

        } catch (err) {
            console.error("Preview failed:", err);
        }
    };

    const handleSelect = async (index) => {
        const doc = documents[index];
        if (!doc?.uuid) return;

        const isSelected = selectedIndexes.includes(index);

        try {
            if (isSelected) {
                setSelectedIndexes(prev => prev.filter(i => i !== index));

                await unapproveLicenseTrialDocument(doc.uploadUuid);
                setHasChanges?.(true); 
                setDocuments(prev => {
                    const copy = [...prev];
                    copy[index] = {
                        ...copy[index],
                        approvedAt: null
                    };
                    return copy;
                });

                Toast.success("Approval removed successfully");
            } else {
                setSelectedIndexes(prev => [...prev, index]);

                await approveLicenseTrialDocument(doc.uploadUuid);
                setHasChanges?.(true); 
                setDocuments(prev => {
                    const copy = [...prev];
                    copy[index] = {
                        ...copy[index],
                        approvedAt: new Date()
                    };
                    return copy;
                });

                Toast.success("Document approved successfully");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const mergedDocuments = requiredDocuments.map((_, index) => {
        return documents[index] || null;
    });

    const documentsToRender =
        mode === MODE_SOURCE.SALES_AND_PROMOTION
            ? mergedDocuments.map((doc, index) => ({
                doc,
                index
            }))
            : mergedDocuments;

    return (
        <>
            {/* HEADER */}
            <RowHeader>
                <SubText weight={700} color={color || "#171A22"}>
                    {title}
                </SubText>
                <CountText
                    size={mode === MODE_SOURCE.SALES_AND_PROMOTION ? "20px" : "16px"}
                    lineHeight={mode === MODE_SOURCE.SALES_AND_PROMOTION ? "22px" : "18px"}
                >
                    {documents.filter(Boolean).length} of {requiredDocuments.length}
                </CountText>
            </RowHeader>

            {/* GRID */}
            <DocumentGrid>
                {documentsToRender.map((reqDoc, index) => {
                    const doc =
                        mode === MODE_SOURCE.SALES_AND_PROMOTION
                            ? reqDoc.doc
                            : reqDoc;

                    const originalIndex =
                        mode === MODE_SOURCE.SALES_AND_PROMOTION
                            ? reqDoc.index
                            : index;

                    const isChecked = !!doc?.approvedAt;
                    const uploadedByName = doc?.uploadedBy?.employeeName;
                    return (
                        <DocumentItem key={doc?.uuid || index}>
                            <TitleRow>
                                <DocumentTitle>
                                    {requiredDocuments[originalIndex]?.description ||
                                        `Document ${originalIndex + 1}`}
                                </DocumentTitle>

                                {mode === MODE_SOURCE.SALES_AND_PROMOTION && (
                                    <ConfirmText> {isChecked ? "Approved" : "Confirm"}</ConfirmText>
                                )}
                            </TitleRow>

                            <div style={{ display: "flex", justifyContent: "space-between", gap: "20px" }}>
                                <div style={{ flex: 1 }}>
                                    <DocumentCard>
                                        {!doc ? (
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <UploadButton
                                                    onClick={() =>
                                                        inputRefs.current[originalIndex]?.click()
                                                    }
                                                >
                                                    <UploadIcon src={AddIcon} />
                                                    <UploadText>Upload File</UploadText>
                                                </UploadButton>
                                                <RadioCircle />
                                               
                                            </div>
                                        ) : (
                                            <FileRow>
                                                <FileName title={doc?.name}>
                                                    {doc?.name}
                                                </FileName>

                                                <ActionRow style={{ gap: "15px" }}>

                                                    {/* DASHBOARD */}
                                                    {mode === MODE_SOURCE.DASHBOARD ? (
                                                        <>
                                                            <IconBtn
                                                                style={{
                                                                    padding: "2px",
                                                                    boxSizing: "border-box"
                                                                }}
                                                            >
                                                                <img
                                                                    onClick={() => openRemoveModal(originalIndex)}
                                                                    src={CloseIcon}
                                                                    alt="close"
                                                                    style={{
                                                                        width: "14px",
                                                                        height: "14px",
                                                                        lineHeight: "2px",
                                                                        objectFit: "contain",
                                                                        marginTop: "-.01px"
                                                                    }}
                                                                />
                                                            </IconBtn>

                                                            <IconBtn
                                                                onClick={() =>
                                                                    inputRefs.current[index]?.click()
                                                                }
                                                            >
                                                                <img src={RedoIcon} style={{ marginTop: "3px" }}/>
                                                            </IconBtn>

                                                            <img
                                                                src={CheckIcon}
                                                                width="26"
                                                                height="26"
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ViewButton onClick={() => handleView(doc)}>
                                                                View
                                                            </ViewButton>

                                                            {mode === MODE_SOURCE.SETTINGS || mode === MODE_SOURCE.SALES_AND_PROMOTION && (
                                                                <IconBtn
                                                                    onClick={() =>
                                                                        inputRefs.current[index]?.click()
                                                                    }
                                                                >
                                                                    <img src={RedoIcon} style={{ marginTop: "3px" }}/>
                                                                </IconBtn>
                                                            )}

                                                            <IconBtn onClick={() => handleDownload(doc)}>
                                                                <img src={DownloadIcon} />
                                                            </IconBtn>
                                                        </>
                                                    )}
                                                </ActionRow>
                                            </FileRow>
                                        )}
                                    </DocumentCard>
                                </div>

                                {/* SALES AND PROMOTION */}
                                {mode === MODE_SOURCE.SALES_AND_PROMOTION && (
                                    <RadioWrapper
                                        onClick={() => handleSelect(originalIndex)}
                                    >
                                        {isChecked ? (
                                            <RadioCheckIcon src={BlueCheck} /> 
                                        ) : (
                                            <RadioCircle />
                                        )}
                                    </RadioWrapper>
                                )}
                            </div>

                            {uploadedByName && (
                                <UploadedByText>
                                     Uploaded By {uploadedByName}
                                </UploadedByText>
                            )}
                            {doc && mode !== MODE_SOURCE.DASHBOARD && (
                                <RemoveButton onClick={() => openRemoveModal(originalIndex)}>
                                    Remove Document
                                </RemoveButton>
                            )}

                            <input
                                type="file"
                                ref={(el) => (inputRefs.current[originalIndex] = el)}
                                style={{ display: "none" }}
                                onChange={(e) =>
                                    handleUpload(originalIndex, e.target.files[0])
                                }
                            />
                        </DocumentItem>
                    );
                })}
            </DocumentGrid>

            {/* PREVIEW MODAL */}
            {previewFile && (
                <ModalOverlay onClick={() => setPreviewFile(null)}>
                    <ModalBox onClick={(e) => e.stopPropagation()}>
                        <ModalClose onClick={() => setPreviewFile(null)}>×</ModalClose>
                        <PreviewImage src={getPreviewUrl(previewFile)} />
                    </ModalBox>
                </ModalOverlay>
            )}

            {/* REMOVE MODAL */}
            {showRemoveModal && (
                <StandardModal
                    open={showRemoveModal}
                    onClose={cancelRemove}
                    title="Remove Document"
                    description="The file will be deleted permanently, and a new file will need to be uploaded to meet requirements."
                    widthMode="medium"
                    footer={(
                        <StandardModalActionGroup $pushRight>
                            <StandardModalActionButton onClick={cancelRemove}>Cancel</StandardModalActionButton>
                            <StandardModalActionButton $variant="primary" onClick={confirmRemove} disabled={loading}>
                                {loading ? "Deleting..." : "Confirm"}
                            </StandardModalActionButton>
                        </StandardModalActionGroup>
                    )}
                >
                    {removeIndex !== null && (
                        <StandardModalInfoCard>
                            <StandardModalLabel as="div">{requiredDocuments[removeIndex]?.description}</StandardModalLabel>
                            <StandardModalText>{documents[removeIndex]?.name}</StandardModalText>
                        </StandardModalInfoCard>
                    )}
                </StandardModal>
            )}
        </>
    );
}
